import React from 'react'

import { Link as GLink, navigate } from 'gatsby'
import { CssBaseline, Link, createTheme } from '@mui/material'

import { PublicClientApplication, EventType } from '@azure/msal-browser'
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate } from '@azure/msal-react'
import { ContextProviderComponent } from './hooks/useUserAccountInformation'
import { CustomNavigationClient } from './utils/NavigationClient'
import { isDevelopment, skipAuthentication } from './utils/config'
import { msalConfig } from './utils/auth'
import { LoginPage } from './components/LoginPage'
import { CoreThemeProvider } from './utils/themeContext'
import { IdleTimeoutManager } from './components/IdleTimeoutManager'

const msalInstance = new PublicClientApplication(msalConfig)
const postLoginRedirectKey = 'cih.postLoginRedirectPath'
let authEventRegistered = false

const clearStoredPostLoginPath = (browserWindow: Window): void => {
  browserWindow.sessionStorage.removeItem(postLoginRedirectKey)
  browserWindow.localStorage.removeItem(postLoginRedirectKey)
}

const getPostLoginPath = (): string => {
  const browserWindow = globalThis.window

  if (browserWindow === undefined) {
    return '/'
  }

  const requestedPath = browserWindow.sessionStorage.getItem(postLoginRedirectKey)
  clearStoredPostLoginPath(browserWindow)
  const defaultPath = '/'

  if (requestedPath === null || requestedPath.trim().length === 0) {
    return defaultPath
  }

  // Prevent open redirects by allowing internal app-relative paths only.
  return requestedPath.startsWith('/') ? requestedPath : defaultPath
}

const LinkBehavior = React.forwardRef<
HTMLAnchorElement,
any
>((props, ref) => {
  const { href, className, children, ...other } = props

  // If href is undefined, render children without link wrapper
  // This prevents Gatsby Link errors while data is loading
  if (!href) {
    return (
      <span className={className}>
        {children}
      </span>
    )
  }

  return (
    <Link
      to={href}
      component={GLink}
      className={className}
      underline='none'
      {...other}
    >
      {children}
    </Link>
  )
})

const unileverTheme = createTheme({
  // Theme palette can be customized here if needed.
  // palette: {
  //   primary: {
  //     light: '#f0f0f0',
  //     main: '#3f50b5',
  //     dark: '#002884',
  //     contrastText: '#fff'
  //   },
  //   secondary: {
  //     light: '#ff7961',
  //     main: '#f44336',
  //     dark: '#ba000d',
  //     contrastText: '#000'
  //   }
  // },
  typography: {
    fontFamily: [
      'Unilever Shilling',
      '-apple-system',
      'BlinkMacSystemFont',
      'Helvetica Neue',
      'sans-serif'
    ].join(' ,'),
    h1: {
      color: '#0400ff'
    }
  },
  components: {
    MuiLink: {
      defaultProps: {
        component: LinkBehavior
      }
    }
  }
})

interface WrapperProps {
  element: JSX.Element
}

const wrapWithProvider = ({ element }: WrapperProps): JSX.Element => {
  // The next 2 lines are optional. This is how you configure MSAL to take advantage of the router's navigate functions when MSAL redirects between pages in your app
  const navigationClient = new CustomNavigationClient()
  msalInstance.setNavigationClient(navigationClient)

  if (!authEventRegistered) {
    msalInstance.addEventCallback((event: any) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        msalInstance.setActiveAccount(event.payload.account)

        const targetPath = getPostLoginPath()
        const browserWindow = globalThis.window
        if (browserWindow !== undefined) {
          const currentPath = `${browserWindow.location.pathname}${browserWindow.location.search}${browserWindow.location.hash}`
          if (targetPath !== currentPath) {
            navigate(targetPath, { replace: true })
          }
        }
      }
    })

    authEventRegistered = true
  }

  return (
    <CoreThemeProvider>
      <MsalProvider instance={msalInstance}>
        {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
        <CssBaseline />

        <AuthenticatedTemplate>
          <IdleTimeoutManager />
          <ContextProviderComponent>
            {element}
          </ContextProviderComponent>
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          {isDevelopment && skipAuthentication ? (
            <ContextProviderComponent>
              {element}
            </ContextProviderComponent>
          ) : (
            <LoginPage />
          )}
        </UnauthenticatedTemplate>
      </MsalProvider>
    </CoreThemeProvider>
  )
}

export default wrapWithProvider
